import { cinemaChain } from "../constants";
import {
  buildCanonicalUrl,
  buildMovieSessionUrl,
  formatCanonicalSegment,
  formatSeoCityName,
  formatSeoRouteLabel,
  normalizeCanonicalPath,
  normalizeSeoText,
} from "./seo";

const BRAND_SCHEMA_CONFIG = {
  PVR: {
    name: "PVR Cinemas",
    url: "https://www.pvrcinemas.com/",
    logo: "https://originserver-static1-uat.pvrcinemas.com/pvrcms/pvrinox/pvr_web-logo.png",
    sameAs: [
      "https://www.facebook.com/moviesatpvr",
      "https://x.com/_PVRCinemas",
      "https://www.instagram.com/pvrcinemas_official/",
      "https://www.youtube.com/user/PVRChannel",
    ],
    telephone: "+91-8800900009",
    twitterHandle: "@_pvrcinemas",
  },
  INOX: {
    name: "INOX Movies",
    url: "https://www.inoxmovies.com/",
    logo: "https://originserver-static1-uat.pvrcinemas.com/pvrcms/pvrinox/inox_web-logo.png",
    sameAs: [
      "https://www.facebook.com/INOXMovies",
      "https://twitter.com/INOXMovies",
      "https://www.instagram.com/inoxmovies",
      "https://www.youtube.com/@INOXMovies",
    ],
    telephone: "+91-22-4062-6900",
    twitterHandle: "@INOXMovies",
  },
};

const DEFAULT_LANGUAGES = [
  "English",
  "Hindi",
  "Marathi",
  "Gujarati",
  "Tamil",
  "Telugu",
  "Kannada",
  "Malayalam",
  "Bengali",
  "Punjabi",
];

const STATIC_BREADCRUMB_LABELS = {
  "/about-us": "About Us",
  "/advertise": "Advertise",
  "/birthday-request": "Birthday Request",
  "/bulk-gift-cards": "Bulk Gift Cards",
  "/bulkbooking/coroporate-booking": "Corporate Booking",
  "/careers-us": "Careers",
  "/cinema-format": "Cinema Format",
  "/coming-soon": "Coming Soon",
  "/coming-soon-movie-calender": "Coming Soon Movie Calendar",
  "/donation-tnc": "Donation Terms & Conditions",
  "/experience": "Experience",
  "/faq": "FAQ",
  "/feedback": "Feedback",
  "/food": "Food",
  "/gift-cards": "Gift Cards",
  "/investors-section": "Investors Section",
  "/kotak-pp-plus": "Kotak PP Plus",
  "/live-movies": "Live Movies",
  "/m.search": "Search",
  "/mobile-map": "Mobile Map",
  "/movie-library": "Movie Library",
  "/movie-on-demand": "Movie On Demand",
  "/news": "News",
  "/offers": "Offers",
  "/passport": "Passport",
  "/privacy-policy": "Privacy Policy",
  "/promocode": "Promocode",
  "/promote-earn": "Promote & Earn",
  "/screenit": "ScreenIT",
  "/select-cinema-for-fnb": "Select Cinema for F&B",
  "/select-food": "Select Food",
  "/show-timings": "Show Timings",
  "/terms-conditions": "Terms & Conditions",
  "/terms-use": "Terms of Use",
};

const stripHtmlTags = (value = "") => String(value || "").replace(/<[^>]*>/g, " ");

export const getBrandSchemaConfig = () =>
  BRAND_SCHEMA_CONFIG[cinemaChain] || BRAND_SCHEMA_CONFIG.PVR;

export const getOrganizationSchema = () => {
  const brandConfig = getBrandSchemaConfig();

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: brandConfig.name,
    url: brandConfig.url,
    logo: brandConfig.logo,
    sameAs: brandConfig.sameAs,
    contactPoint: {
      "@type": "ContactPoint",
      telephone: brandConfig.telephone,
      contactType: "customer service",
      areaServed: "IN",
      availableLanguage: DEFAULT_LANGUAGES,
    },
  };
};

export const getWebsiteSchema = () => {
  const brandConfig = getBrandSchemaConfig();

  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: brandConfig.name,
    url: brandConfig.url,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${brandConfig.url}?s={search_term_string}`,
      },
      "query-input": {
        "@type": "PropertyValueSpecification",
        valueRequired: "http://schema.org/True",
        valueName: "search_term_string",
      },
    },
  };
};

export const getSpeakableSchema = ({ name = "", url = "" } = {}) => {
  const brandConfig = getBrandSchemaConfig();

  return {
    "@context": "https://schema.org/",
    "@type": "WebPage",
    name: name || brandConfig.name,
    speakable: {
      "@type": "SpeakableSpecification",
      xpath: [
        "/html/head/title",
        "/html/head/meta[@name='description']/@content",
      ],
    },
    url: url || brandConfig.url,
  };
};

export const getBreadcrumbSchema = (items = []) => {
  if (!Array.isArray(items) || items.length <= 1) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.item,
    })),
  };
};

export const getFaqSchema = (faqs = []) => {
  const normalizedFaqs = (faqs || [])
    .map((item) => ({
      question: normalizeSeoText(item?.question || item?.name),
      answer: normalizeSeoText(
        stripHtmlTags(item?.answer || item?.awnser || item?.acceptedAnswer?.text)
      ),
    }))
    .filter((item) => item.question && item.answer);

  if (!normalizedFaqs.length) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: normalizedFaqs.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
};

export const getMovieItemListSchema = ({
  name = "",
  url = "",
  movies = [],
  city = "",
  limit = 5,
} = {}) => {
  const brandConfig = getBrandSchemaConfig();
  const schemaItems = (movies || [])
    .filter((movie) => movie?.id && (movie?.n || movie?.filmName))
    .slice(0, limit)
    .map((movie, index) => {
      const movieName = normalizeSeoText(movie?.n || movie?.filmName);

      return {
        "@type": "Movie",
        position: index + 1,
        name: movieName,
        url: city
          ? buildMovieSessionUrl(city, movieName, movie.id)
          : buildCanonicalUrl("/"),
        image: movie?.miv || movie?.mih || brandConfig.logo,
      };
    });

  if (!schemaItems.length) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    url: url || brandConfig.url,
    numberOfItems: schemaItems.length,
    itemListElement: schemaItems,
  };
};

export const getRouteBreadcrumbItems = (pathname = "/") => {
  const normalizedPath = normalizeCanonicalPath(pathname);
  const pathSegments = normalizedPath.split("/").filter(Boolean);
  const homeItem = {
    name: "Home",
    item: buildCanonicalUrl("/"),
  };

  if (!pathSegments.length) {
    return [homeItem];
  }

  const [firstSegment, secondSegment, thirdSegment, fourthSegment] = pathSegments;

  if (firstSegment === "cinemas" && secondSegment) {
    const cityName = formatSeoCityName(secondSegment);
    return [
      homeItem,
      {
        name: `Cinemas in ${cityName}`,
        item: buildCanonicalUrl(["cinemas", formatCanonicalSegment(secondSegment)]),
      },
    ];
  }

  if (firstSegment === "experience" && secondSegment && thirdSegment) {
    const cityName = formatSeoCityName(secondSegment);
    return [
      homeItem,
      {
        name: `Luxury Cinema Experience in ${cityName}`,
        item: buildCanonicalUrl([
          "experience",
          formatCanonicalSegment(secondSegment),
        ]),
      },
      {
        name: formatSeoRouteLabel(thirdSegment),
        item: buildCanonicalUrl(normalizedPath),
      },
    ];
  }

  if (firstSegment === "experience" && secondSegment) {
    const cityName = formatSeoCityName(secondSegment);
    return [
      homeItem,
      {
        name: `Luxury Cinema Experience in ${cityName}`,
        item: buildCanonicalUrl([
          "experience",
          formatCanonicalSegment(secondSegment),
        ]),
      },
    ];
  }

  if (
    firstSegment === "cinemasessions" &&
    secondSegment &&
    thirdSegment &&
    fourthSegment
  ) {
    const cityName = formatSeoCityName(secondSegment);
    return [
      homeItem,
      {
        name: `Cinemas in ${cityName}`,
        item: buildCanonicalUrl(["cinemas", formatCanonicalSegment(secondSegment)]),
      },
      {
        name: formatSeoRouteLabel(thirdSegment),
        item: buildCanonicalUrl(normalizedPath),
      },
    ];
  }

  if (firstSegment === "cinemasessions" && secondSegment && thirdSegment) {
    return [
      homeItem,
      {
        name: "Show Timings",
        item: buildCanonicalUrl("/show-timings"),
      },
      {
        name: formatSeoRouteLabel(secondSegment),
        item: buildCanonicalUrl(normalizedPath),
      },
    ];
  }

  if (
    firstSegment === "moviesessions" &&
    secondSegment &&
    thirdSegment &&
    fourthSegment
  ) {
    const cityName = formatSeoCityName(secondSegment);
    return [
      homeItem,
      {
        name: "Show Timings",
        item: buildCanonicalUrl("/show-timings"),
      },
      {
        name: `${formatSeoRouteLabel(thirdSegment)} in ${cityName}`,
        item: buildCanonicalUrl(normalizedPath),
      },
    ];
  }

  if (firstSegment === "moviesessions" && secondSegment && thirdSegment) {
    return [
      homeItem,
      {
        name: "Show Timings",
        item: buildCanonicalUrl("/show-timings"),
      },
      {
        name: formatSeoRouteLabel(secondSegment),
        item: buildCanonicalUrl(normalizedPath),
      },
    ];
  }

  return [
    homeItem,
    {
      name:
        STATIC_BREADCRUMB_LABELS[normalizedPath] ||
        formatSeoRouteLabel(pathSegments[pathSegments.length - 1]),
      item: buildCanonicalUrl(normalizedPath),
    },
  ];
};

export const getRouteSchemaPageName = (pathname = "/") => {
  const breadcrumbItems = getRouteBreadcrumbItems(pathname);
  return breadcrumbItems[breadcrumbItems.length - 1]?.name || getBrandSchemaConfig().name;
};
